import { Box } from '@chakra-ui/react';
import React, { useState, useMemo, useCallback } from 'react';

import { AliasMatchingFilters } from './AliasMatchingFilters';
import {
  AliasMatchingTablesContainer,
  filterFacilitiesBySearch,
} from './AliasMatchingTable';
import { AutoMatchAcrossFacilities } from './AutoMatchAcrossFacilities';
import { MatchStatusCardsContainer } from './MatchStatusCards';
import { MatchStatus } from './types';
import { useAutoMatchSuggestions } from './useAutoMatchSuggestions';
import { useGetLimitedPartnerFacilitiesForLenderQuery } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';

export const AliasMatching = () => {
  const { user } = useUserAuthOrThrow();

  const {
    data: facilitiesData,
    error: facilitiesError,
    refetch: refetchFacilities,
  } = useGetLimitedPartnerFacilitiesForLenderQuery({
    variables: { lenderOrganizationId: user.organizationId },
    fetchPolicy: 'cache-and-network',
  });

  if (facilitiesError) throw facilitiesError;

  const [matchStatusForTables, setMatchStatusForTables] = useState<MatchStatus>(
    'all',
  );

  const [searchQuery, setSearchQuery] = useState('');

  const facilities = useMemo(() => facilitiesData?.limitedPartnerFacilities, [
    facilitiesData,
  ]);

  const searchedFacilities = useMemo(
    () =>
      facilities
        ? filterFacilitiesBySearch(facilities, searchQuery)
        : undefined,
    [facilities, searchQuery],
  );

  const {
    autoMatchesByFacility,
    loadSuggestions,
    isLoading: isLoadingSuggestions,
    markMatched,
  } = useAutoMatchSuggestions(user.organizationId);

  // "ready to auto-match" view: only facilities that had suggestions when they
  // were loaded (they stay listed once matched, showing as done)
  const [isReadyOnly, setIsReadyOnly] = useState(false);

  const handleShowReady = useCallback(async (): Promise<void> => {
    const loaded = await loadSuggestions();
    if (loaded) {
      setIsReadyOnly(true);
    }
  }, [loadSuggestions]);

  const listedFacilities = useMemo(
    () =>
      isReadyOnly && autoMatchesByFacility
        ? searchedFacilities?.filter((facility) =>
            autoMatchesByFacility.has(facility.creditFacilityId),
          )
        : searchedFacilities,
    [isReadyOnly, autoMatchesByFacility, searchedFacilities],
  );

  return (
    <Box padding={dashboardRoutePadding}>
      <Breadcrumbs
        breadcrumbs={[{ label: 'Limited Partners', path: `/limited-partners` }]}
      />
      <Typography variant="page-title" TextProps={{ mt: '1.5rem' }}>
        Match LP Alias
      </Typography>
      <MatchStatusCardsContainer
        facilities={facilities}
        matchStatusForTables={matchStatusForTables}
        setMatchStatusForTables={setMatchStatusForTables}
      />
      <AliasMatchingFilters setSearchQuery={setSearchQuery} />
      {listedFacilities && (
        <AutoMatchAcrossFacilities
          facilities={listedFacilities}
          searchQuery={searchQuery}
          autoMatchesByFacility={autoMatchesByFacility}
          isLoadingSuggestions={isLoadingSuggestions}
          isReadyOnly={isReadyOnly}
          onShowReady={handleShowReady}
          onShowAll={() => setIsReadyOnly(false)}
          onMappingMatched={markMatched}
          refetchFacilities={refetchFacilities}
        />
      )}
      <AliasMatchingTablesContainer
        facilities={listedFacilities}
        lenderOrganizationId={user.organizationId}
        matchStatusForTables={matchStatusForTables}
        refetchFacilities={refetchFacilities}
        autoMatchesByFacility={autoMatchesByFacility}
        onMappingMatched={markMatched}
      />
    </Box>
  );
};
